/**
 * FixedList —— 定高行虚拟列表（会话列表等「顶部锚定」列表用）。
 * 语义与选型理由见 ./fixedRange.ts 文件头；聊天时间线（不定高/反向/锚点保持）
 * 用 VirtualList，两者不要混用。
 *
 * 复用 virtualList.module.scss 的 viewport/spacer/row：容器滚动 + 单 spacer
 * 撑总高 + 绝对定位行，overflow-anchor 关闭（定高列表不需要浏览器锚定，
 * 行为跨浏览器一致）。
 */
import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { fixedRange } from './fixedRange';
import styles from './virtualList.module.scss';

export interface FixedListProps<T> {
  items: readonly T[];
  /** 行 key：稳定且唯一（会话用 ConversationKey） */
  itemKey: (item: T) => string;
  renderItem: (item: T) => ComponentChildren;
  /** 行高 px，所有行必须同高（行组件 CSS 保证定高+溢出截断） */
  itemHeight: number;
  /** 可视区外预渲染像素，默认 320 */
  overscanPx?: number;
  className?: string;
}

export function FixedList<T>(props: FixedListProps<T>) {
  const { items, itemKey, renderItem, itemHeight, overscanPx = 320, className } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportH, setViewportH] = useState(0);

  // 视口高度跟踪（窗口缩放/软键盘）；RO 初始回调即首次测量
  useLayoutEffect(() => {
    const el = containerRef.current!;
    const ro = new ResizeObserver(() => setViewportH(el.clientHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { start, end, totalHeight } = fixedRange(
    scrollTop,
    viewportH,
    itemHeight,
    items.length,
    overscanPx,
  );
  const rows: ComponentChildren[] = [];
  for (let i = start; i < end; i += 1) {
    const item = items[i]!;
    rows.push(
      <div
        key={itemKey(item)}
        className={styles.row}
        style={{ top: `${i * itemHeight}px`, height: `${itemHeight}px` }}
      >
        {renderItem(item)}
      </div>,
    );
  }

  return (
    <div
      ref={containerRef}
      className={className ? `${styles.viewport} ${className}` : styles.viewport}
      data-testid="fixed-list"
      onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
    >
      <div className={styles.spacer} style={{ height: `${totalHeight}px` }} />
      {rows}
    </div>
  );
}
